import { getCoverThumbUrl } from '../../utils/coverUrl';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, BookOpen as BookOpenIcon } from 'lucide-react';

const getCoverUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${window.location.origin}${url}`;
  return url;
};

// Drag-aware carousel (extracted from BookDetails)
const DragScrollCarousel = ({ children, className = '' }) => {
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll, children]);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };
  const onMouseUp = () => {
    isDragging.current = false;
    scrollRef.current.style.cursor = 'grab';
    scrollRef.current.style.userSelect = '';
  };
  const scrollBy = (direction) => {
    scrollRef.current?.scrollBy({ left: direction * 400, behavior: 'smooth' });
  };

  return (
    <div className={`relative group/carousel ${className}`}>
      {canScrollLeft && (
        <button
          onClick={() => scrollBy(-1)}
          className="absolute left-0 top-0 bottom-8 w-12 z-10 bg-gradient-to-r from-black/80 to-transparent flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity"
          data-testid="carousel-scroll-left"
        >
          <ChevronLeft className="h-8 w-8 text-white" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scrollBy(1)}
          className="absolute right-0 top-0 bottom-8 w-12 z-10 bg-gradient-to-l from-black/80 to-transparent flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity"
          data-testid="carousel-scroll-right"
        >
          <ChevronRight className="h-8 w-8 text-white" />
        </button>
      )}
      <div
        ref={scrollRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
        style={{ cursor: 'grab', scrollbarWidth: 'none' }}
      >
        {children}
      </div>
    </div>
  );
};

export { DragScrollCarousel };

const SeriesContinuation = ({ seriesBooks, currentBookId, navigate }) => {
  if (!seriesBooks?.series_name || !(seriesBooks.books?.length > 1)) return null;
  return (
    <div className="bg-gradient-to-b from-black/65 to-black/70 backdrop-blur-sm py-12" data-testid="series-section">
      <div className="max-w-screen-2xl mx-auto px-8">
        <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
          <BookOpenIcon className="h-6 w-6 mr-3 text-red-500" />
          More from the "{seriesBooks.series_name}" Series
        </h2>
        <p className="text-gray-400 mb-6 text-sm">
          {seriesBooks.total_in_series} books in this series
        </p>
        <DragScrollCarousel>
          {seriesBooks.books.map((sBook) => {
            const isCurrent = sBook.id === currentBookId;
            return (
              <div
                key={sBook.id}
                className={`flex-shrink-0 w-48 group relative transition-transform duration-200 select-none ${
                  isCurrent ? 'ring-2 ring-red-500 rounded-lg opacity-80' : 'hover:scale-105'
                }`}
                data-testid={`series-book-${sBook.series_order || sBook.id}`}
              >
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={getCoverThumbUrl(sBook.cover_image_url)}
                    alt={sBook.title}
                    className="w-48 h-72 object-cover rounded-lg pointer-events-none"
                    loading="lazy"
                    draggable="false"
                  />
                  <div className="absolute top-2 left-2 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                    Book {sBook.series_order || '?'}
                  </div>
                  {isCurrent && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg">
                      <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Currently Viewing
                      </span>
                    </div>
                  )}
                  {!isCurrent && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors rounded-lg flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/book/${sBook.id}`);
                        }}
                        className="bg-white text-black text-xs font-bold px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200"
                        data-testid={`series-view-${sBook.id}`}
                      >
                        View Book
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-white text-sm font-medium mt-2 line-clamp-2">{sBook.title}</p>
                {sBook.rating > 0 && (
                  <p className="text-yellow-400 text-xs mt-1">
                    <span className="mr-1">&#9733;</span>
                    {sBook.rating?.toFixed(1)}
                  </p>
                )}
              </div>
            );
          })}
        </DragScrollCarousel>
      </div>
    </div>
  );
};

export default SeriesContinuation;
